import { type MediaPublicSubmissionOption, type RoleOption } from './models/Org';
import { faXTwitter, faYoutube, faInstagram, faTiktok } from '@fortawesome/free-brands-svg-icons';
import { faGlobeAmericas, faPencil } from '@fortawesome/pro-light-svg-icons';
import { faBrowser } from '@fortawesome/pro-regular-svg-icons';
import { faCloudUploadAlt } from '@fortawesome/free-solid-svg-icons';

// REGEX
export const REGEX_PASSWORD =
  '^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[\\^$*.\\[\\]{}()?\\-"!@#%&/\\\\,><\':;|_~`])(?=.{8,99})';
export const URL_REGEX =
  /^(http:\/\/www\.|https:\/\/www\.|http:\/\/|https:\/\/)?[a-z0-9]+([-.]{1}[a-z0-9]+)*\.[a-z]{2,5}(:[0-9]{1,5})?(\/.*)?$/i;
export const URL_PROTOCOL = /https?:\/\//i;
export const LAST_URL_PARAM = /\/[^/]*$/;

// Messages
export const MSG_PASSWORD_REQUIREMENT =
  'Password must be at least 8 characters and include uppercase, lowercase, numbers and at least one of the following special characters: ^ $ * . [ ] { } ( ) ? - " ! @ # % & /  , > < \' : ; | _ ~ `';

export const PINNED_MESSAGE = 'Successfully pinned';
export const UNPINNED_MESSAGE = 'Successfully unpinned';

export const MediaPublicSubmission = {
  IMAGE: 'image',
  VIDEO: 'video',
  BOTH: 'both',
};

export const MediaPublicSubmissionLabels = {
  [MediaPublicSubmission.IMAGE]: 'Image',
  [MediaPublicSubmission.VIDEO]: 'Video',
  [MediaPublicSubmission.BOTH]: 'Image & Video',
};

export const Roles = {
  GIGG_ADMIN: 'giggAdmin',
  ADMIN: 'admin',
  MEMBER: 'member',
  VIEWER: 'viewer',
};

export const RoleLabels = {
  [Roles.ADMIN]: 'Admin',
  [Roles.MEMBER]: 'Member',
  [Roles.VIEWER]: 'Viewer',
};

export const RestMethods = {
  POST: 'POST',
  GET: 'GET',
  PUT: 'PUT',
  DELETE: 'DELETE',
};

export const MutateStatus = {
  IDLE: 'idle',
  LOADING: 'loading',
  ERROR: 'error',
  SUCCESS: 'success',
};

export const RoleSelections = [
  { value: Roles.ADMIN, label: RoleLabels[Roles.ADMIN] },
  { value: Roles.MEMBER, label: RoleLabels[Roles.MEMBER] },
] as RoleOption[];

export const BoardRoleSelections = [
  ...RoleSelections,
  { value: Roles.VIEWER, label: RoleLabels[Roles.VIEWER] },
] as RoleOption[];

export const MediaPublicSummissionSelections = [
  { value: MediaPublicSubmission.IMAGE, label: MediaPublicSubmissionLabels[MediaPublicSubmission.IMAGE] },
  { value: MediaPublicSubmission.VIDEO, label: MediaPublicSubmissionLabels[MediaPublicSubmission.VIDEO] },
  { value: MediaPublicSubmission.BOTH, label: MediaPublicSubmissionLabels[MediaPublicSubmission.BOTH] },
] as MediaPublicSubmissionOption[];

export const Services = {
  YOUTUBE: 'youtube',
  INSTAGRAM: 'instagram',
  TWITTER: 'twitter',
  CUSTOM: 'custom',
  CUSTOM_PUBLIC: 'customPublic',
  INSTAGRAM_GRAPH: 'instagramGraph',
  FACEBOOK: 'facebook',
  OPEN_GRAPH: 'openGraph',
  GIGG: 'gigg',
  TIK_TOK: 'tikTok',
};

export const ServicesCommonName = {
  [Services.TWITTER]: Services.TWITTER,
  [Services.INSTAGRAM]: Services.INSTAGRAM,
  [Services.INSTAGRAM_GRAPH]: Services.INSTAGRAM,
  [Services.YOUTUBE]: Services.YOUTUBE,
  [Services.CUSTOM]: Services.CUSTOM,
  [Services.FACEBOOK]: Services.FACEBOOK,
  [Services.OPEN_GRAPH]: Services.OPEN_GRAPH,
  [Services.GIGG]: Services.GIGG,
  [Services.TIK_TOK]: Services.TIK_TOK,
};

export const BillingStatuses = {
  PAYMENT_SUCCEEDED: 'invoice.payment_succeeded',
  PAYMENT_FAILED: 'invoice.payment_failed',
  CANCELED: 'customer.subscription.deleted',
};

export const RequestStatuses = {
  SUCCESS: 'SUCCESS',
  FAILURE: 'FAILURE',
  REQUEST: 'REQUEST',
};

export const EntityTypes = {
  BOARD: 'board',
  COMPETITION: 'competition',
};

export const EntityAliases = {
  BOARD: 'inbox',
  COMPETITION: 'competition',
};

export const UI_TEXT = {
  [EntityTypes.COMPETITION]: 'Challenge',
};

export const EntitySubTypes = {
  UPLOADS: 'UPLOADS',
  SOCIAL: 'SOCIAL',
  COMMENTS: 'COMMENTS',
  SOCIAL_SITE: 'SOCIAL_SITE',
  BRACKET: 'BRACKET',
};

export const VisionLikelihood = {
  VERY_UNLIKELY: {
    CODE: 'VERY_UNLIKELY',
    SEVERITY: 1,
  },
  UNLIKELY: {
    CODE: 'UNLIKELY',
    SEVERITY: 2,
  },
  POSSIBLE: {
    CODE: 'POSSIBLE',
    SEVERITY: 3,
  },
  LIKELY: {
    CODE: 'LIKELY',
    SEVERITY: 4,
  },
  VERY_LIKELY: {
    CODE: 'VERY_LIKELY',
    SEVERITY: 5,
  },
};

export const EntityNav = {
  [EntityTypes.BOARD]: {
    INBOX: {
      label: 'Social Inbox',
      route: '',
    },
    PINNED: {
      label: 'Pinned',
      route: 'board',
    },
    ANALYTICS: {
      label: 'Analytics',
      route: 'analytics',
    },
    SETTINGS: {
      label: 'Inbox Settings',
      route: 'settings',
    },
  },
  [EntityTypes.COMPETITION]: {
    INBOX: {
      label: 'Submissions',
      route: '',
    },
    PINNED: {
      label: 'Rounds Breakdown',
      route: 'submissions',
    },
    ANALYTICS: {
      label: 'Analytics',
      route: 'analytics',
    },
    SETTINGS: {
      label: `${UI_TEXT.competition} Settings`,
      route: 'settings',
    },
  },
  undefined: {
    INBOX: {
      label: 'Social Inbox',
      route: '',
    },
    PINNED: {
      label: 'Pinned',
      route: 'pinned',
    },
    ANALYTICS: {
      label: 'Analytics',
      route: 'analytics',
    },
    SETTINGS: {
      label: 'Inbox Settings',
      route: 'settings',
    },
  },
};

export const AdvancementTypes = {
  SUBMISSION: 'SUBMISSION',
  VOTING: 'VOTING',
  SELECTED: 'SELECTED',
};

export const SubmissionTypes = {
  SOCIAL: 'SOCIAL',
  UPLOADS: 'UPLOADS',
  COMMENTS: 'COMMENTS',
  SOCIAL_SITE: 'SOCIAL_SITE',
  BRACKET: 'BRACKET',
};

export const MediaFilterTypes = {
  BOTH: 'BOTH',
  IMAGE: 'IMAGE',
  VIDEO: 'VIDEO',
  TEXT: 'TEXT',
};

export const BrandIconMap = {
  gigg: {
    icon: faCloudUploadAlt,
  },
  youtube: {
    icon: faYoutube,
  },
  twitter: {
    icon: faXTwitter,
  },
  instagram: {
    icon: faInstagram,
  },
  instagramGraph: {
    icon: faInstagram,
  },
  custom: {
    icon: faPencil,
  },
  customPublic: {
    icon: faGlobeAmericas,
  },
  openGraph: {
    icon: faBrowser,
  },
  tikTok: {
    icon: faTiktok,
  },
};

export const MediaTypes = {
  BOTH: 'BOTH',
};

export const LocalStorage = {
  deletePostsHideConfirmation: 'deletePostsHideConfirmation',
  unPublishModalConfirmation: 'unPublishModalConfirmation',
  disqualifyEntrant: 'disqualifyEntrant',
};

export const Apis = {
  CORE: 'core',
  ANALYTICS: 'analytics-v1',
};

export const metricIds = {
  ENGAGEMENTS: 'specific/engagements',
  LOCATIONS_USA: 'shared/locations/usa',
  LOCATIONS_WORLD: 'shared/locations/world',
  IMPRESSIONS: 'shared/impressions',
  VIEWERS: 'shared/viewers',
  FUNNELS: 'specific/funnels',
  OTHER_BUTTONS_CLICKED: 'specific/engagements/buttons',
  CTA_BUTTON_CLICKED: 'shared/engagements/ctas',
  SOCIAL_SHARES: 'shared/engagements/shares',
  POSTS_OPENED: 'shared/engagements/posts',
  BOARD_INTERACTION_COUNT: 'specific/engagements/interactions',
  BOARD_ENGAGEMENT_TIMES: 'specific/engagements/times',
  BOARD_VIEWS_VS_PAGE_LOADS: 'shared/impressions/views',
  TIME_ON_BOARD: 'shared/impressions/times',
  REFERRING_LINK: 'shared/referrers',
  TIME_ON_SITE: 'specific/conversions/times',
  CARD_FLIPPED_WITH_CTA: 'specific/conversions/focus',
  ENTRY_COUNT: 'specific/entrants/kpi',
  ENTRANT_LOCATION_USA: 'specific/entrants/locations/usa',
  ENTRANT_LOCATION_WORLD: 'specific/entrants/locations/world',
  TOP_REFERRING_POSTS: 'shared/posts/referringitems',
  ENTRANT_TIMELINE: 'specific/entrants/timeline',
  COMPETITION_ENGAGEMENT: 'specific/entrants',
  VIDEO_VIEW_COUNT: 'specific/engagements/videoviews',
  TIME_ON_SITE_COMPETITION: 'specific/engagements/sitetime',
  VOTE_COUNT: 'specific/voters/kpi',
  TOTAL_VOTES: 'specific/voters/votecount',
  VOTER_LOCATION_USA: 'specific/voters/locations/usa',
  VOTER_LOCATION_WORLD: 'specific/voters/locations/world',
  UNIQUE_RETURNING: 'specific/voters/comparison',
  AVG_VISITS: 'specific/voters/avgvisits',
  TOP_ENTRIES: 'specific/standings',
};

export const mapScopes = {
  STATES: 'states',
  WORLD: 'world',
};

export const postsShownValues = {
  ALL: 'all',
  PINNED: 'pinned',
  UNPINNED: 'unpinned',
};

export const orderValues = {
  DESC: 'desc',
  ASC: 'asc',
};

export const FACEBOOK_SCOPE =
  'business_management,instagram_basic,pages_manage_metadata,pages_read_engagement,pages_show_list,instagram_manage_comments,email,public_profile';

export const keywordStatuses = {
  FETCHING: 2001,
  SUCCESS: 2000,
  FAILURE: 1000,
};

export const ABLY_EVENTS = {
  KEYWORD_PULL_STATUS: 'KEYWORD_PULL_STATUS',
  COMPETITION_MEDIA_UPDATE: 'COMPETITION_MEDIA_UPDATE',
};

export const videoValues = {
  FILE_HOST_URL: 'https://competition-assets.gigg.com',
  APPLE_VIDEO: 'video/quicktime',
};

export const competitionStates = {
  NOT_STARTED: 'Not Started',
  IN_PROGRESS: 'In Progress',
  WINNERS_STAGE: 'Winners Stage',
  IS_FINISHED: 'Finished',
};

export const CAPTCHA_TYPE = 'WEB';

export const TWITTER_AUTH_LOCAL_KEY = 'twitterAuth';
